// src/services/auditHistoryService.js
import { repo } from "../dbservice/repo";

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════

const COL_HISTORY = "auditeventhistory";

// ═══════════════════════════════════════════════════════════════════════════════
// INTERNAL HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

function _getLocalClient() {
  try {
    if (!repo._hasValidLocalConfig()) return null;
    return repo._getLocalSupabaseClient();
  } catch {
    return null;
  }
}

const _log = (...args) => {
  if (process.env.NODE_ENV !== "production") {
    console.log("[auditHistoryService]", ...args);
  }
};

// Normalize a raw history row into the canonical shape used by the UI.
function _normalizeHistoryRow(row) {
  if (!row) return row;
  const auditor = row.auditor_email || row.audited_by_name || row.metadata?.updatedBy || "";
  const auditedAt = row.audited_at || row.metadata?.updatedAt || row.updated_at || null;

  return {
    id:             row.id             ?? null,
    audit_event_id: row.audit_event_id ?? null,
    assetId:        row.assetId        ?? row.asset_id    ?? null,
    asset_id:       row.assetId        ?? row.asset_id    ?? null,
    assetName:      row.assetName      ?? row.asset_name  ?? "",
    asset_name:     row.assetName      ?? row.asset_name  ?? "",
    categoryId:     row.categoryId     ?? row.category_id ?? row.category ?? null,
    category_id:    row.categoryId     ?? row.category_id ?? row.category ?? null,
    brandId:        row.brandId        ?? row.brand_id    ?? row.brand    ?? null,
    brand_id:       row.brandId        ?? row.brand_id    ?? row.brand    ?? null,
    companyId:      row.companyId      ?? row.company_id  ?? null,
    locationCode:   row.locationCode   ?? row.location_code ?? "",
       status:               row.status                ?? "pending",
    remark:               row.remark                ?? row.notes       ?? "",
    auditor_email:        auditor,
    audited_by_name:      auditor,
    audited_at:           auditedAt,
    tagVerificationPhoto: row.tagVerificationPhoto  ?? row.tag_verification_photo ?? null,
    metadata: {
      createdAt:   row.metadata?.createdAt   ?? row.created_at   ?? null,
      createdBy:   row.metadata?.createdBy   ?? row.created_by   ?? null,
      updatedAt:   row.metadata?.updatedAt   ?? row.updated_at   ?? null,
      updatedBy:   row.metadata?.updatedBy   ?? row.updated_by   ?? null,
      deletedFlag: row.metadata?.deletedFlag ?? false,
    },
  };
}

// One-shot in-memory lookup join 
async function _hydrateHistoryRows(rows, companyId) {
  if (!rows.length || !companyId) return rows;

  const client = _getLocalClient();

  const fetchRef = async (name) => {
    if (client) {
      const { data, error } = await client
        .from(name)
        .select("*")
        .eq("companyId", companyId);
      if (error) { _log(`hydrate ${name} error`, error.message); return []; }
      return data ?? [];
    }
    try {
      return (await repo._fetchDataNative(
        name,
        [{ field: "companyId", value: companyId }],
        [],
        null,
      )) ?? [];
    } catch {
      return [];
    }
  };

  const [assets, categories, brands, locations] = await Promise.all([
    fetchRef("assets"),
    fetchRef("categories"),
    fetchRef("brands"),
    fetchRef("locations"),
  ]);

  const assetMap = new Map(assets.map(a => [String(a.id), a]));
  const catMap   = new Map(categories.map(c => [String(c.id), c]));
  const brandMap = new Map(brands.map(b => [String(b.id), b]));
  const locMap   = new Map(
    locations.map(l => [String(l.locationCode ?? l.id), l]),
  );

  return rows.map((r) => {
    const asset = assetMap.get(String(r.asset_id)) || {};

    const catId = r.category_id || asset.categoryId || asset.category || null;
    const brandId = r.brand_id || asset.brandId || asset.brand || null;

    const cat = catMap.get(String(catId)) || {};
    const brand = brandMap.get(String(brandId)) || {};
    const loc = locMap.get(String(r.locationCode)) || {};

    const getCategoryName =
      cat.name || cat.categoryName || cat.title || cat.label || cat.value ||
      asset.categoryName || asset.category || asset.details?.category ||
      (typeof cat === 'string' ? cat : "") ||
      (catId ? `ID: ${catId}` : "");

    const getBrandName =
      brand.name || brand.brandName || brand.title || brand.label || brand.value ||
      asset.brandName || asset.brand || asset.details?.brand ||
      (typeof brand === 'string' ? brand : "") ||
      (brandId ? `ID: ${brandId}` : "");

    return {
      ...r,
      asset_tag: asset.assetTag || asset.details?.assetTag || asset.assetId || asset.id || "",
      category_name: getCategoryName,
      brand_name: getBrandName,
      location_name: loc.name || loc.locationName || "",
    };
  });
}

function _buildSeedRows({
  auditEventId,
  assets,
  companyId,
  locationCode,
  createdBy,
  now,
}) {
  return assets.map((asset) => ({
    audit_event_id: auditEventId,
    assetId:        asset.id,
    assetName:      asset.assetName ?? asset.name ?? "",
    categoryId:     asset.categoryId ?? asset.category ?? null,
    brandId:        asset.brandId ?? asset.brand ?? null,
    companyId,
    locationCode:   locationCode ?? "",
    status:         "pending",
    remark:         "",
    auditor_email:  "",
    metadata: {
      createdAt:   now,
      createdBy:   createdBy ?? "system",
      updatedAt:   now,
      updatedBy:   "system",
      deletedFlag: false,
    },
  }));
}

// ═══════════════════════════════════════════════════════════════════════════════
// FETCH — SINGLE EVENT
// ═══════════════════════════════════════════════════════════════════════════════
export async function fetchAuditEventAssets(auditEventId, companyId) {
  if (!auditEventId) throw new Error("fetchAuditEventAssets: auditEventId required");
  if (!companyId)    throw new Error("fetchAuditEventAssets: companyId required");

  const client = _getLocalClient();

  if (client) {
    const { data, error } = await client
      .from(COL_HISTORY)
      .select("*")
      .eq("audit_event_id", auditEventId)
      .eq("companyId", companyId)
      .order("assetName", { ascending: true });

    if (error) throw error;

    const normalized = (data ?? []).map(_normalizeHistoryRow);
    return _hydrateHistoryRows(normalized, companyId);
  }

  const rows = await repo._fetchDataNative(
    COL_HISTORY,
    [{ field: "audit_event_id", value: auditEventId }],
    [],
    null,
  );

  const normalized = (rows ?? [])
    .filter((r) => (r.companyId ?? r.company_id) === companyId)
    .sort((a, b) =>
      (a.assetName ?? a.asset_name ?? "").localeCompare(
        b.assetName ?? b.asset_name ?? "",
      ),
    )
    .map(_normalizeHistoryRow);

  return _hydrateHistoryRows(normalized, companyId);
}

// ═══════════════════════════════════════════════════════════════════════════════
// FETCH — BATCH
// ═══════════════════════════════════════════════════════════════════════════════

export async function fetchAuditHistoryForEvents(eventIds, client = undefined) {
  if (!Array.isArray(eventIds) || eventIds.length === 0) return new Map();

  const resolvedClient = client === undefined ? _getLocalClient() : client;

  if (resolvedClient) {
    const { data: allHistory, error } = await resolvedClient
      .from(COL_HISTORY)
      .select(
        "id, audit_event_id, assetId, assetName, status, remark, auditor_email, metadata",
      )
      .in("audit_event_id", eventIds);

    if (error) throw error;

    const byEvent = new Map();
    for (const row of allHistory ?? []) {
      const key = row.audit_event_id;
      if (!byEvent.has(key)) byEvent.set(key, []);
      byEvent.get(key).push(_normalizeHistoryRow(row));
    }
    return byEvent;
  }

  const historyByEvent = await Promise.all(
    eventIds.map((eventId) =>
      repo
        ._fetchDataNative(
          COL_HISTORY,
          [{ field: "audit_event_id", value: eventId }],
          [],
          null,
        )
        .then((rows) => ({
          eventId,
          rows: (rows ?? []).map(_normalizeHistoryRow),
        }))
        .catch(() => ({ eventId, rows: [] })),
    ),
  );

  return new Map(historyByEvent.map(({ eventId, rows }) => [eventId, rows]));
}

// ═══════════════════════════════════════════════════════════════════════════════
// SEED
// ═══════════════════════════════════════════════════════════════════════════════

export async function seedAuditHistory({
  auditEventId,
  assets,
  companyId,
  locationCode = "",
  createdBy = "",
}) {
  if (!auditEventId) throw new Error("seedAuditHistory: auditEventId required");
  if (!Array.isArray(assets) || assets.length === 0) {
    throw new Error("seedAuditHistory: at least one asset is required");
  }

  const client = _getLocalClient();
  const now    = new Date().toISOString();

  const rows = _buildSeedRows({
    auditEventId, assets, companyId, locationCode, createdBy, now,
  });

  if (client) {
    const { error } = await client.from(COL_HISTORY).insert(rows);
    if (error) throw error;
    return rows.length;
  }

  await Promise.all(
    rows.map((row, idx) => {
      const histId = `${auditEventId}_${row.assetId ?? idx}`;
      return repo._upsertDataCloud(COL_HISTORY, histId, { ...row, id: histId });
    }),
  );

  return rows.length;
}

// ═══════════════════════════════════════════════════════════════════════════════
// REPLACE
// ═══════════════════════════════════════════════════════════════════════════════

export async function replaceAuditHistory({
  auditEventId,
  assets,
  companyId,
  locationCode = "",
  createdBy = "",
  client = undefined,
}) {
  if (!auditEventId) throw new Error("replaceAuditHistory: auditEventId required");

  const resolvedClient = client === undefined ? _getLocalClient() : client;
  const now = new Date().toISOString();

  if (resolvedClient) {
    const { error: delError } = await resolvedClient
      .from(COL_HISTORY)
      .delete()
      .eq("audit_event_id", auditEventId);

    if (delError) throw delError;

    if (Array.isArray(assets) && assets.length > 0) {
      const rows = _buildSeedRows({
        auditEventId, assets, companyId, locationCode, createdBy, now,
      });
      const { error: insError } = await resolvedClient
        .from(COL_HISTORY)
        .insert(rows);
      if (insError) throw insError;
    }
    return;
  }

  const existing = await repo._fetchDataNative(
    COL_HISTORY,
    [{ field: "audit_event_id", value: auditEventId }],
    [],
    null,
  );

  await Promise.all(
    (existing ?? []).map((h) => repo.deleteData(COL_HISTORY, h.id)),
  );

  if (Array.isArray(assets) && assets.length > 0) {
    const rows = _buildSeedRows({
      auditEventId, assets, companyId, locationCode, createdBy, now,
    });
    await Promise.all(
      rows.map((row, idx) => {
        const histId = `${auditEventId}_${row.assetId ?? idx}`;
        return repo._upsertDataCloud(COL_HISTORY, histId, {
          ...row,
          id: histId,
        });
      }),
    );
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// UPSERT 
// ═══════════════════════════════════════════════════════════════════════════════

export async function upsertAuditHistory(record) {
  if (!record.id)             throw new Error("upsertAuditHistory: record.id required");
  if (!record.audit_event_id) throw new Error("upsertAuditHistory: audit_event_id required");

  const client = _getLocalClient();
  const now    = new Date().toISOString();

  const isRealAuditAction =
    record.status && record.status !== "pending";

  if (client) {
    const { data: existing, error: fetchErr } = await client
      .from(COL_HISTORY)
      .select("auditor_email, metadata")
      .eq("id", record.id)
      .maybeSingle();

    if (fetchErr) throw fetchErr;

    const existingMeta         = existing?.metadata || {};
    const existingAuditorEmail = existing?.auditor_email || "";

    const auditorEmail = isRealAuditAction
      ? (existingAuditorEmail || record.auditor_email || "")
      : "";

    const mergedMeta = {
      createdAt:   existingMeta.createdAt   || record.metadata?.createdAt   || now,
      createdBy:   existingMeta.createdBy   || record.metadata?.createdBy   || "system",
      updatedAt:   now,
      updatedBy:   record.metadata?.updatedBy || "system",
      deletedFlag: existingMeta.deletedFlag ?? record.metadata?.deletedFlag ?? false,
    };

    const enriched = {
      id:             record.id,
      audit_event_id: record.audit_event_id,
      assetId:        record.assetId        ?? null,
      assetName:      record.assetName      ?? "",
      categoryId:     record.categoryId     ?? null,
      brandId:        record.brandId        ?? null,
      companyId:      record.companyId      ?? null,
      locationCode:   record.locationCode   ?? "",
      status:         record.status         ?? "pending",
      remark:         record.remark         ?? "",
      auditor_email:  auditorEmail,
      tagVerificationPhoto: record.tagVerificationPhoto ?? null, 
      metadata:       mergedMeta,
    };

    const { data, error } = await client
      .from(COL_HISTORY)
      .upsert(enriched, { onConflict: "id" })
      .select()
      .single();

    if (error) throw error;
    return _normalizeHistoryRow(data);
  }

  let existingRow = null;
  try {
    const rows = await repo._fetchDataNative(
      COL_HISTORY,
      [{ field: "id", value: record.id }],
      [],
      null,
    );
    existingRow = rows?.[0] ?? null;
  } catch {
    /* first write — no existing row */
  }

  const existingMeta         = existingRow?.metadata || {};
  const existingAuditorEmail = existingRow?.auditor_email || "";

  const auditorEmail = isRealAuditAction
    ? (existingAuditorEmail || record.auditor_email || "")
    : "";

  const mergedMeta = {
    createdAt:   existingMeta.createdAt   || record.metadata?.createdAt   || now,
    createdBy:   existingMeta.createdBy   || record.metadata?.createdBy   || "system",
    updatedAt:   now,
    updatedBy:   record.metadata?.updatedBy || "system",
    deletedFlag: existingMeta.deletedFlag ?? record.metadata?.deletedFlag ?? false,
  };

  const enriched = {
    id:             record.id,
    audit_event_id: record.audit_event_id,
    assetId:        record.assetId        ?? existingRow?.assetId   ?? null,
    assetName:      record.assetName      ?? existingRow?.assetName ?? "",
    categoryId:     record.categoryId     ?? existingRow?.categoryId ?? null,
    brandId:        record.brandId        ?? existingRow?.brandId    ?? null,
    companyId:      record.companyId      ?? existingRow?.companyId  ?? null,
    locationCode:   record.locationCode   ?? existingRow?.locationCode ?? "",
    status:         record.status         ?? "pending",
    remark:         record.remark         ?? "",
    auditor_email:  auditorEmail,
    tagVerificationPhoto:
      record.tagVerificationPhoto ??
      existingRow?.tagVerificationPhoto ??
      null,
    metadata:       mergedMeta,
  };

  await repo._upsertDataCloud(COL_HISTORY, record.id, enriched);
  return _normalizeHistoryRow(enriched);
}

// ═══════════════════════════════════════════════════════════════════════════════
// UPDATE STATUS
// ═══════════════════════════════════════════════════════════════════════════════

export async function updateAuditHistoryStatus({
  historyRowId,
  auditEventId,
  assetId,
  assetName,
  companyId,
  locationCode,
  auditorEmail,
  auditorName,
  auditorId,
  newStatus,
  remark = "",
  updatedBy = "",
  tagVerificationPhoto = null,  
}) {
  if (!historyRowId) throw new Error("updateAuditHistoryStatus: historyRowId required");
  if (!auditEventId) throw new Error("updateAuditHistoryStatus: auditEventId required");
  if (!companyId)    throw new Error("updateAuditHistoryStatus: companyId required");
  if (!newStatus)    throw new Error("updateAuditHistoryStatus: newStatus required");

  const effectiveAuditor = auditorEmail || auditorName || auditorId || updatedBy || "";

  return upsertAuditHistory({
    id:             historyRowId,
    audit_event_id: auditEventId,
    assetId,
    assetName:      assetName ?? "",
    companyId,
    locationCode:   locationCode ?? "",
    status:         newStatus,
    remark,
    auditor_email:  effectiveAuditor,
    tagVerificationPhoto, 
    metadata: {
      updatedBy: updatedBy || effectiveAuditor || "system",
    },
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// DELETE
// ═══════════════════════════════════════════════════════════════════════════════

export async function deleteAuditHistoryRow(historyRowId) {
  if (!historyRowId) throw new Error("deleteAuditHistoryRow: historyRowId required");

  const client = _getLocalClient();

  if (client) {
    const { error } = await client
      .from(COL_HISTORY)
      .delete()
      .eq("id", historyRowId);
    if (error) throw error;
    return { success: true, id: historyRowId };
  }

  await repo.deleteData(COL_HISTORY, historyRowId);
  _log("deleteAuditHistoryRow", historyRowId);
  return { success: true, id: historyRowId };
}

export async function deleteAuditHistoryForEvent(auditEventId) {
  if (!auditEventId) throw new Error("deleteAuditHistoryForEvent: auditEventId required");

  const client = _getLocalClient();

  if (client) {
    const { error } = await client
      .from(COL_HISTORY)
      .delete()
      .eq("audit_event_id", auditEventId);
    if (error) throw error;
    return { success: true, audit_event_id: auditEventId };
  }

  const existing = await repo._fetchDataNative(
    COL_HISTORY,
    [{ field: "audit_event_id", value: auditEventId }],
    [],
    null,
  );

  await Promise.all(
    (existing ?? []).map((h) => repo.deleteData(COL_HISTORY, h.id)),
  );

  _log(
    "deleteAuditHistoryForEvent",
    auditEventId,
    `(${existing?.length ?? 0} rows)`,
  );
  return { success: true, audit_event_id: auditEventId };
}